import type { DeepPartial } from "@apollo/client/utilities";
import { type Readable, type Subscriber, type Unsubscriber, get } from "svelte/store";
import { CantPayOnlineReason } from "@core/schema/CantPayOnlineReason.js";
import type { Order } from "@core/schema/Order.js";
import type { ProductGift } from "@core/schema/OrderProduct.js";
import { PaymentType } from "@core/schema/PaymentType.js";
import type { Product } from "@core/schema/Product.js";
import { maxOnlinePrice } from "@core/schema/maxOnlinePrice.js";
import { isSameOrderProduct } from "@core/schema/utils/isSameOrderProduct.js";
import { transformProductToOrderProduct } from "@core/schema/utils/transformProductToOrderProduct.js";
import { deepMerge } from "@core/utils/deepMerge.js";
import { formatPrice } from "@core/utils/formatPrice.js";
import type { LocalStorageValue } from "@core/utils/svelte/localStorageState.svelte.js";
import { type LocalStorageStore, localStorageWritable } from "@core/utils/svelte/localStorageWritable.js";
import type { AddOrderProductResponse } from "./AddOrderProductResponse.js";
import type { Cart, CartInStep } from "./Cart.js";
import { CartStep, cartStepArray, getNextStep } from "./CartStep.js";
import { cartVersion } from "./cartVersion.js";
import { cartWithAppliedProperties } from "./cartWithAppliedProperties.js";
import { ensureCartIsInCorrectStep } from "./ensureCartIsInCorrectStep.js";
import type { FieldsToFillInPerStep } from "./fieldsToFillInPerStep.js";

export class CartStore implements Readable<LocalStorageValue<Cart>> {
	private readonly store: LocalStorageStore<Cart>;

	private constructor(
		private readonly mainId: Product["id"] | undefined,
		cart: Cart,
	) {
		this.store = localStorageWritable(`cart-${cartVersion}`, cart);
	}

	public subscribe(run: Subscriber<LocalStorageValue<Cart>>, invalidate?: () => void): Unsubscriber {
		return this.store.subscribe(run, invalidate);
	}

	private async update<T extends Cart = Cart>(updater: (value: T) => Promise<T> | T): Promise<T> {
		const current = get(this.store);
		const afterUpdate = await updater(current.value as T);

		return new Promise((resolve) => {
			this.store.update(() => {
				const cartWithProperties = cartWithAppliedProperties(afterUpdate);
				const cartWithEnsuredCorrectStep = ensureCartIsInCorrectStep(cartWithProperties);

				resolve(cartWithEnsuredCorrectStep);
				return cartWithEnsuredCorrectStep;
			});
		});
	}

	public removeProduct(productIndex: number): Promise<Cart> {
		return this.update((cart: Cart): Cart => {
			const orderProductToRemove = cart.order.products[productIndex];
			if (!orderProductToRemove) {
				throw new Error("Index is out of bounds");
			}

			if (orderProductToRemove.productId === this.mainId) {
				return CartStore.initialCart;
			}

			cart.order.products.splice(productIndex, 1);

			const noMoreProductOfIdInCart = !cart.order.products.some(
				({ productId }) => productId === orderProductToRemove.productId,
			);

			if (noMoreProductOfIdInCart) {
				cart.productsInOrder = cart.productsInOrder.filter(({ id }) => id !== orderProductToRemove.productId);
			}

			if (cart.order.products.length === 0) {
				return CartStore.initialCart;
			}

			return cart;
		});
	}

	public setProductQuantity(productIndex: number, quantity: number): Promise<Cart> {
		return this.update((cart) => {
			if (quantity > 0) {
				const orderProduct = cart.order.products[productIndex];
				if (!orderProduct) {
					throw new Error("Index is out of bounds");
				}
				orderProduct.quantity = quantity;

				return cart;
			} else {
				return this.removeProduct(productIndex);
			}
		});
	}

	public async saveFields<CurrentCart extends Cart = Cart>(
		fields: DeepPartial<CurrentCart["order"]>,
	): Promise<CurrentCart> {
		return await this.update<CurrentCart>((cart) => {
			cart.order = deepMerge(cart.order, fields);
			return cart;
		});
	}

	public async advanceToNextStep<Step extends CartStep>(
		currentStep: Step,
		fields: Pick<CartInStep<Step>["order"], FieldsToFillInPerStep<Step>>,
		onAdvance?: (cart: Cart) => void,
	): Promise<boolean> {
		await this.saveFields(fields);

		const step = {
			advanced: false,
			canAdvance: false,
		};

		const updated = await this.update((cart) => {
			if (cart.order.products.length === 0) {
				return cart;
			}

			const cartStepIndex = cartStepArray.indexOf(cart.step);
			const nextStep = getNextStep(currentStep);
			const nextStepIndex = cartStepArray.indexOf(nextStep);
			if (cartStepIndex < nextStepIndex) {
				cart.step = nextStep;
				step.advanced = true;
			}
			step.canAdvance = true;
			return cart;
		});

		if (step.advanced) {
			onAdvance?.(updated);
		}
		return step.canAdvance;
	}

	public async increaseQuantity(productIndex: number): Promise<void> {
		await this.update((cart) => {
			const orderProduct = cart.order.products[productIndex];
			if (!orderProduct) {
				throw new Error("Index is out of bounds");
			}
			return this.setProductQuantity(productIndex, orderProduct.quantity + 1);
		});
	}

	public async decreaseQuantity(productIndex: number): Promise<void> {
		await this.update((cart) => {
			const orderProduct = cart.order.products[productIndex];
			if (!orderProduct) {
				throw new Error("Index is out of bounds");
			}
			return this.setProductQuantity(productIndex, orderProduct.quantity - 1);
		});
	}

	public async addProduct(product: Product, quantity: number, gifts?: ProductGift[]): Promise<AddOrderProductResponse> {
		let response = {} as AddOrderProductResponse;
		await this.update((cart) => {
			const productToAdd = transformProductToOrderProduct(crypto.randomUUID(), product, quantity, gifts);
			const isFirstProductOfId = !cart.order.products.find(({ productId }) => productId === productToAdd.productId);
			if (isFirstProductOfId) {
				cart.productsInOrder.push(product);
			}

			const existingProduct = cart.order.products.find((orderProduct) =>
				isSameOrderProduct(orderProduct, productToAdd),
			);

			if (existingProduct) {
				const oldQuantity = existingProduct.quantity;
				existingProduct.quantity += quantity;
				response = {
					type: "increment",
					item: productToAdd,
					quantity: existingProduct.quantity - oldQuantity,
				};
			} else {
				cart.order.products.push(productToAdd);
				response = {
					type: "add",
					item: productToAdd,
					quantity: productToAdd.quantity,
				};
			}

			return cart;
		});
		return response;
	}

	public async updateItem(productIndex: number, product: Product): Promise<void> {
		await this.update((cart) => {
			const orderProduct = cart.order.products[productIndex];
			if (!orderProduct) {
				throw new Error("Index is out of bounds");
			}

			const productInOrderIndex = cart.productsInOrder.findIndex(({ id }) => id === product.id);
			if (productInOrderIndex === -1) {
				throw new Error("Product not found in productsInOrder");
			}

			cart.productsInOrder[productInOrderIndex] = product;
			cart.order.products[productIndex] = transformProductToOrderProduct(
				orderProduct.id,
				product,
				orderProduct.quantity,
			);

			return cart;
		});
	}

	public async setPacketaPoint(packetaPoint: Packeta.Point | null): Promise<Cart> {
		return await this.update((cart) => {
			cart.packetaPoint = packetaPoint;
			return cart;
		});
	}

	public async markAsSent(order: Order): Promise<Cart> {
		return await this.update((cart) => {
			if (cart.step !== CartStep.ReadyToSend) {
				throw new Error("The cart is not ready to be sent");
			}
			cart.sentOrder = order;
			return cart;
		});
	}

	public static initial(mainId?: Product["id"]): CartStore {
		return new this(mainId, this.initialCart);
	}

	public static fromCart(cart: Cart, mainId: Product["id"]): CartStore {
		return new this(mainId, cart);
	}

	private static readonly initialCart = cartWithAppliedProperties<Cart>({
		productsInOrder: [],
		packetaPoint: null,
		properties: {
			cantPayOnlineReasons: [],
		},
		order: {
			products: [],
			payment: {
				type: PaymentType.GoPayPaymentCard,
			},
		},
		step: CartStep.Overview,
	});

	public clear(): void {
		this.store.set(CartStore.initialCart);
	}
}
